import { createContext, useContext } from 'react'
import type { HTMLAttributes } from 'react'
import { elementStyle } from './style'
import type { CmsLayout, CmsSection } from './types'

/* The contexts the storefront reads its content through.
 *
 * Kept out of the provider's own file for the reason every other context in
 * this codebase is: a module exporting both a component and a plain value
 * breaks fast refresh.
 */

export type CmsState = {
  layout: CmsLayout | null
  /** True inside the console's preview frame. Turns on click-to-select and
      the hover outlines, and nothing else — the page renders identically. */
  preview: boolean
  /** What the console has selected, so the frame can outline it. */
  selected: { section: string | null; element: string | null }
}

export const CmsContext = createContext<CmsState>({
  layout: null,
  preview: false,
  selected: { section: null, element: null },
})

export const useCms = () => useContext(CmsContext)

/** The section being rendered, put here by SectionFrame. */
export const CmsSectionContext = createContext<CmsSection | null>(null)

/* ---------- what a section component reads ---------- */

/** One repeating thing inside a section — an assurance, a step, a reason.

    The shape is the same whether it came from the database or from the
    component's own fallback list, so a component maps over one kind of
    object either way. */
export type SectionItem = {
  id: string
  content: Record<string, string>
  /** Spread onto the element's own tag: its colour overrides, the flags that
      switch on its rules, and — in the preview frame — the hooks the console
      selects it by. */
  props: HTMLAttributes<HTMLElement>
}

export type SectionApi = {
  key: string
  /** One content field, or the value the component was written with. */
  text: (field: string, fallback?: string) => string
  /** A comma-separated field as a list — the hero's breadcrumb. */
  list: (field: string, fallback?: string[]) => string[]
  /** The elements that are switched on, in the order the console put them. */
  items: (fallback: { id: string; content: Record<string, string> }[]) => SectionItem[]
  /** Attributes marking a piece of copy as selectable in the preview frame. */
  field: (name: string) => Record<string, string>
}

const NO_PROPS: Record<string, string> = {}

/** Everything a section component needs to draw itself from the CMS.
 *
 *  Every read takes the value the component was originally written with as
 *  its fallback. That is not belt and braces — it is what a CMS outage looks
 *  like. The API being unreachable leaves the site reading exactly as it did
 *  before any of this existed, rather than as a page of empty headings. It
 *  also keeps each default legible at the place it is used.
 */
export function useSection(): SectionApi {
  const section = useContext(CmsSectionContext)
  const { preview, selected } = useCms()

  const key = section?.key ?? ''

  const text = (field: string, fallback = '') => {
    const given = section?.content?.[field]
    return given === undefined ? fallback : given
  }

  return {
    key,

    text,

    list: (field, fallback = []) => {
      const given = section?.content?.[field]
      if (given === undefined) return fallback
      const parts = given
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
      return parts.length > 0 ? parts : []
    },

    items: (fallback) => {
      const source =
        section?.elements && section.elements.length > 0
          ? section.elements.filter((element) => element.enabled)
          : fallback.map((item) => ({ ...item, enabled: true, style: {} }))

      return source.map((element) => {
        const { style, flags } = elementStyle(
          (element as { style?: Record<string, string> }).style,
        )
        return {
          id: element.id,
          content: element.content,
          props: {
            ...flags,
            style,
            /* Only in the frame: outside it these would be dead attributes on
               every card on a public page. */
            ...(preview
              ? {
                  'data-cms-el': element.id,
                  'data-cms-state':
                    selected.section === key && selected.element === element.id
                      ? 'selected'
                      : undefined,
                }
              : {}),
          } as HTMLAttributes<HTMLElement>,
        }
      })
    },

    field: (name) => (preview ? { 'data-cms-field': name } : NO_PROPS),
  }
}
